import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ConfirmDialog, EmptyState } from '@/shared/components';
import {
  toApiError,
  useApi,
  type ParkingSpotResponse,
  type PropertyGroupResponse,
  type SaveParkingSpotRequest,
} from '@/shared/api';
import { ObjectParkingCards } from './ObjectParkingCards';
import { ObjectParkingForm, emptyParkingDraft, type ParkingDraft } from './ObjectParkingForm';
import { ParkingListLoadError } from './parking-occupancy/ParkingListLoadError';
import { parkingProblemText } from './parking-occupancy/parkingProblemText';

export function ObjectParkingSection({ group }: { group: PropertyGroupResponse }) {
  const api = useApi();
  const client = useQueryClient();
  const key = ['property-groups', group.id, 'parking-spots'];
  const apartmentIds = group.apartments.map((apartment) => apartment.id);
  const spots = useQuery({ queryKey: key, queryFn: () => api.parking.listSpots(group.id) });
  const properties = useQuery({
    queryKey: ['properties', 'parking-assignments', group.id],
    queryFn: () => api.properties.list({ pageSize: 100, propertyIds: apartmentIds }),
    enabled: apartmentIds.length > 0,
  });
  const [draft, setDraft] = useState(emptyParkingDraft);
  const [editing, setEditing] = useState<ParkingSpotResponse | null>(null);
  const [deleting, setDeleting] = useState<ParkingSpotResponse | null>(null);
  useEffect(() => {
    setEditing(null);
    setDraft(emptyParkingDraft());
  }, [group.id]);

  const save = useMutation({
    mutationFn: (request: SaveParkingSpotRequest) =>
      editing
        ? api.parking.updateSpot(group.id, editing.id, request)
        : api.parking.createSpot(group.id, request),
    onSuccess: async () => {
      setEditing(null);
      setDraft(emptyParkingDraft());
      await client.invalidateQueries({ queryKey: key });
      toast.success('Parkplatz gespeichert');
    },
    onError: showError,
  });
  const remove = useMutation({
    mutationFn: (spotId: string) => api.parking.deleteSpot(group.id, spotId),
    onSuccess: async () => {
      setDeleting(null);
      await client.invalidateQueries({ queryKey: key });
      toast.success('Parkplatz entfernt');
    },
    onError: showError,
  });
  const propertyNames = new Map(
    (properties.data?.items ?? []).map((property) => [
      property.id,
      property.adminDisplayName ?? property.name,
    ]),
  );

  function edit(spot: ParkingSpotResponse) {
    setEditing(spot);
    setDraft({
      label: spot.label,
      price: String(spot.price),
      currency: spot.currency,
      priceUnit: spot.priceUnit,
      isActive: spot.isActive,
      assignedPropertyIds: [...spot.assignedPropertyIds],
    });
  }
  function submit(value: ParkingDraft) {
    save.mutate({ ...value, price: Number(value.price) });
  }
  async function showError(error: Error) {
    toast.error(
      parkingProblemText(await toApiError(error), 'Der Parkplatz wurde nicht gespeichert.'),
    );
  }

  return (
    <section className="mt-6 space-y-4">
      <div>
        <h2 className="text-ink font-semibold">Gemeinsame Parkplätze</h2>
        <p className="text-ink-soft mt-1 text-sm">
          Ein Stellplatz gilt nur für die hier zugewiesenen Apartments dieses Objekts.
        </p>
      </div>
      {/* Ohne geladene Liste weder Formular noch „keine Parkplätze“: beides wäre geraten. */}
      {!spots.data && !spots.isLoading && (
        <ParkingListLoadError
          query={spots}
          fallback="Die Parkplätze konnten nicht geladen werden."
        />
      )}
      {spots.data && (
        <ObjectParkingForm
          draft={draft}
          editing={!!editing}
          properties={apartmentIds.map((id) => ({ id, name: propertyNames.get(id) ?? id }))}
          saving={save.isPending}
          minimumCharge={
            spots.data && {
              amount: spots.data.minimumChargeAmount,
              currency: spots.data.currency || 'EUR',
            }
          }
          onChange={setDraft}
          onSubmit={submit}
          onCancel={() => {
            setEditing(null);
            setDraft(emptyParkingDraft());
          }}
        />
      )}
      {spots.isLoading ? (
        <p className="text-ink-soft text-sm">Lade Parkplätze…</p>
      ) : !spots.data ? null : spots.data.spots.length ? (
        <ObjectParkingCards
          spots={spots.data.spots}
          propertyNames={propertyNames}
          onEdit={edit}
          onDelete={setDeleting}
        />
      ) : (
        <EmptyState
          icon="Pin"
          title="Noch keine Parkplätze"
          description="Legen Sie den ersten gemeinsamen Stellplatz für dieses Objekt an."
        />
      )}
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={`Parkplatz „${deleting?.label ?? ''}“ entfernen?`}
        description="Der Stellplatz wird für neue Reservierungen ausgeblendet."
        confirmLabel="Entfernen"
        loading={remove.isPending}
        onConfirm={async () => {
          if (deleting) await remove.mutateAsync(deleting.id);
        }}
      />
    </section>
  );
}
